import Backbone from "backbone";
import Promise from "bluebird";
import loadRecaptchaScript from "scripts/functions/loadRecaptchaScript";
import inject from "scripts/ioc/inject";
import BaseFormView from "scripts/views/baseFormView";
import BaseModel from "scripts/models/baseModel";

import templateLoginForm from "templates/loginForm.hbs";

const chan = Backbone.Radio.channel;

class LoginFormView extends BaseFormView {
  get template() {
    return templateLoginForm;
  }

  get recaptchaSiteKey() {
    return process.env.PATRON_UI_RECAPTCHA_SITE_KEY;
  }

  get constraints() {
    return {
      username: {
        presence: true,
      },
      password: {
        presence: true,
      },
    };
  }

  get events() {
    return {
      "click #bb-login-form-forgot-button": "onClickForgot",
      "click #bb-login-button": "handleLoginClick",
    };
  }

  constructor(options, securityService = inject("securityService"), i18nextService = inject("i18nextService")) {
    super(options);

    this.securityService = securityService;
    this.i18nextService = i18nextService;
    this.recaptchaWidgetId = null;

    this.bindModel(
      new BaseModel(
        {},
        {
          constraints: this.constraints,
        },
      ),
    );
  }

  render() {
    const { ariaLabel, placeholder } = this.i18nextService.getAttributes();
    const { actions } = this.i18nextService.getCommon();
    const { auth } = this.i18nextService.getGlossary();
    this.$el.html(
      this.template({
        i18n: {
          explore: {
            ariaLabel: ariaLabel.explore.singular,
            singular: actions.explore.singular,
          },
          forgotPassword: auth.forgotPassword.question,
          placeholder,
        },
      }),
    );

    this.loaderView.appendTo(this.$el.find(".bb-form-submit-button-loader-region")).render();

    this.focusUsernameField();

    return this;
  }

  focusUsernameField() {
    // Only focus on username field when it's visible on page load
    if (chan("display").request("isLargerThanXs")) {
      $("#bb-login-form-username-field").focus();
    }
  }

  async handleLoginClick(e) {
    e.preventDefault();
    if (this.recaptchaRendered()) {
      window.grecaptcha.enterprise.execute(this.recaptchaWidgetId);
    } else {
      this.onSubmit(null);
    }
  }

  renderRecaptcha() {
    this.recaptchaWidgetId = window.grecaptcha.enterprise.render("bb-login-form-recaptcha", {
      sitekey: this.recaptchaSiteKey,
      action: "LOGIN",
      size: "invisible",
      badge: "inline",
      callback: token => this.onSubmit(token),
    });
  }

  recaptchaRendered() {
    return !$("#bb-login-form-recaptcha").is(":empty");
  }

  syncRecaptcha() {
    if (window?.grecaptcha?.enterprise) {
      return Promise.resolve();
    } else {
      return loadRecaptchaScript();
    }
  }

  showRecaptcha() {
    chan("display").request("showBlockingLoader");

    this.syncRecaptcha()
      .bind(this)
      .catch(error => {
        console.error("Something went wrong trying to load recaptcha libs: %O", error);
      })
      .finally(() => {
        chan("display").request("hideBlockingLoader");
        this.renderRecaptcha();
      });
  }

  resetCaptcha() {
    if (window?.grecaptcha?.enterprise) {
      window.grecaptcha.enterprise.reset();
    }
  }

  onClickForgot() {
    this.trigger("forgot");
  }

  async onSubmit(recaptchaToken) {
    let errors = this.validate();

    if (!errors) {
      this.model.set(this.toJSON());
      this.enableSubmit(false);
      this.showLoader();

      this.doSubmit(recaptchaToken)
        .then(this.onSubmitSucceeded.bind(this), this.onSubmitFailed.bind(this))
        .finally(this.hideLoader.bind(this));
    }

    return false;
  }

  onSubmitFailed(error) {
    // Login failed and API is forcing CAPTCHA
    if (error && error.xhr && error.xhr.responseJSON && error.xhr.responseJSON.captcha) {
      if (this.recaptchaRendered()) {
        this.recaptchaToken = null;
        this.resetCaptcha();
      } else {
        this.showRecaptcha();
      }
    }

    this.enableSubmit(true);
    this.trigger("failure", error);
  }

  doSubmit(recaptchaToken = null) {
    return this.securityService.loginWithUsernameAndPassword({
      username: this.model.get("username"),
      password: this.model.get("password"),
      captcha: recaptchaToken,
    });
  }

  // Override to re-enable the submit button on success
  onSubmitSucceeded(value) {
    this.resetCaptcha(); // Remove Recaptcha iframe
    this.enableSubmit(true);
    this.trigger("success", value);
  }

  getPasswordFieldValue() {
    return $("#bb-login-form-password-field").val();
  }
}

export default LoginFormView;
